import { create } from "zustand";
import {
  getInboxFilters,
  getInboxTags,
  getUserMessageTemplates,
  getInboxCampaigns,
  getInboxConversationsList,
  getContactModel,
  deleteContactTag,
  addContactTag,
  setContactStar,
  getContactThreads,
  checkUserGroupDefault,
  sendInterviewScheduling,
  downloadContact,
  deleteScheduledMessage ,
  sendReply,
  shortenUrl,
  cancelScheduledMessage as cancelScheduledMessageApi,
  cancelWhatsAppHold as cancelWhatsAppHoldApi,
  sendScheduledMessageNow as sendScheduledMessageNowApi,
  muteContactEventReminders as muteContactEventRemindersApi,
  readContactNotifications,
  getSequenceTemplates,
  getCompanyGroups,
  addContactToGroups,
  removeContactFromGroups,
  createGroup,
  bulkAddContactTags,
  addGroupContacts,
  setContactsArchived as setContactsArchivedApi,
  translateThread as translateThreadApi,
  updateContactLanguage as updateContactLanguageApi,
} from "./resources";
import { socket, joinRoom, leaveRoom as ioLeaveRoom } from '../../io.js';
import type { Channel } from '../../utils/channels';

/** ADR-025: per-send channel routing. `fallback` only matters when the WhatsApp window is closed. */
export interface SendMessageOpts {
  channel?: Channel;
  fallback?: 'sms' | 'template' | 'hold';
}

interface InboxV2State {
  companyId: string;
  filters: any[];
  campaigns: any[];
  tags: any[];
  groups: any[];
  userMessageTemplates: any[];
  userSequenceTemplates: any[];
  conversations: any[];
  threads: any[];
  activeContact: string;
  activeContactModel: { [key: string]: any };
  currentRoom: string;
  currentConversationRoom: string;
  calendarData: any[];
  /** ADR-025: channel the composer is currently targeting; null = thread default. */
  composerChannel: Channel | null;
  setComposerChannel: (channel: Channel | null) => void;
  updateSelectedCompany: (newCompany: string) => void;
  retrieveFilters: (companyId: string) => Promise<void>;
  retrieveTags: (companyId: string) => Promise<void>;
  retrieveCampaigns: (companyId: string) => Promise<void>;
  retrieveGroups: (companyId: string) => Promise<void>;
  retrieveUserMessageTemplates:()=>Promise<void>;
  retrieveSequenceTemplates:()=>Promise<void>;
  retrieveContactThreads: (contactId: string, page: number) => Promise<boolean>;
  clearThreads: () => void;
  retrieveConversations: (
    companyId: string,
    filters: any[],
    page: number,
    pageSize: number
  ) => Promise<boolean>;
  clearConversations: () => void;
  removeConversation: (contactId: string) => void;
  pushThread:(newThread:{[key:string]:any},socketEvent?:any)=> void;
  convoUpdate:(contact:{[key:string]:any})=> void;
  updateConversationWithNewMessage: (contactId: string, messageData: any, direction?: string) => void;
  setActiveContact: (contactId: string) => void;
  deleteContactTag: (tagId: string) => Promise<boolean>;
  deleteScheduledMessageThread: (schedMsgId:string)=>Promise<boolean>;
  cancelScheduledMessage: (schedMsgId: string) => Promise<boolean>;
  sendScheduledMessageNow: (schedMsgId: string) => Promise<boolean>;
  muteContactEventReminders: (contactId: string, muted: boolean) => Promise<boolean>;
  dropEnrollmentForContact: (contactId: string, moduleId: string) => Promise<boolean>;
  applyScheduledMessageSocketEvent: (event: string, payload: any) => void;
  addContactTag: (tag: string) => Promise<boolean>;
  toggleContactStar: () => Promise<void>;
  userHasCalendar:()=>Promise<any[]>;
  sendCalendarInvite: (contactMobile:string,contactId:string, customMessage?:string, schedulingUserId?:string, translation?:{ lang: string; alt_lang: string; alt_text: string } | null)=>Promise<{ [key: string]: any }>;
  sendMessage: (body:string,botmapId:string,contactId:string,contactMobile:string,moduleGroup?:string,translation?:{ lang: string; alt_lang: string; alt_text: string } | null,opts?:SendMessageOpts)=>Promise<{[key:string]:any}>;
  /** ADR-025: cancel a WhatsApp message held for the candidate's reply. */
  cancelWhatsAppHold: (pendingId: string) => Promise<boolean>;
  // AI translation (ADR-035)
  applyThreadTranslation: (payload: { threadId: string; translation: any }) => void;
  applyContactLanguageUpdated: (payload: { contactId: string; language: string | null; source?: string | null }) => void;
  setContactLanguage: (language: string | null) => Promise<boolean>;
  translateThreadOnDemand: (threadId: string, targetLang?: string) => Promise<any>;
  downloadContactPDF: (contactId:string)=>Promise<{ [key: string]: any }>;
  shortenUrl: (url: string) => Promise<string | null>;
  markConversationAsRead: (contactId: string) => Promise<number>;
  companyGroups: any[];
  fetchCompanyGroups: () => Promise<void>;
  addContactToGroup: (groupId: string) => Promise<boolean>;
  removeContactFromGroup: (groupId: string) => Promise<boolean>;
  createGroupAndAddContact: (name: string) => Promise<boolean>;
  applyContactTagsUpdated: (payload: { contactId: string; companyId?: string; tags: any[] }) => void;
  applyContactGroupsUpdated: (payload: { contactId: string; groups: any[] }) => void;
  applyContactStarUpdated: (payload: { contactId: string; star: boolean }) => void;
  applyUserAvatarUpdated: (payload: { userId: string; avatar_url: string | null; avatar_type?: string }) => void;
  applyTemplateCreated: (template: any) => void;
  applyTemplateUpdated: (template: any) => void;
  applyTemplateDeleted: (templateId: string) => void;
  applySequenceCreated: (sequence: any) => void;
  applySequenceUpdated: (sequence: any) => void;
  applySequenceDeleted: (sequenceId: string) => void;
  // Inbox multi-select (bulk "Add to group" / "Add tag"). Keyed by contact_id
  // and holding the name so the action bar can label selections after rows
  // reorder (socket) or scroll out (infinite scroll).
  selectedContacts: Map<string, SelectedContact>;
  toggleContactSelected: (row: SelectedContact) => void;
  setContactsSelected: (rows: SelectedContact[], selected: boolean) => void;
  clearSelection: () => void;
  bulkAddTagToSelected: (value: string) => Promise<BulkTagResult | null>;
  bulkAddSelectedToGroup: (groupId: string, groupName?: string) => Promise<boolean>;
  bulkCreateGroupWithSelected: (name: string) => Promise<boolean>;
  // Per-user inbox Archive (ADR-034). The page owns the selected menu key and
  // filters, so the store mirrors the last list query to know which view is
  // showing and how to refetch page 1 when a row must (re)appear.
  lastListQuery: { companyId: string; filters: any[]; pageSize: number } | null;
  // Bumped when rows are removed from the current view (archive/unarchive).
  // MessageList owns paging and reloads page 1 on change: removed rows shift
  // the server offsets (page 2 would skip rows) and an emptied list never
  // scrolls, so InfiniteScroll would sit on its loader forever.
  listReloadToken: number;
  requestListReload: () => void;
  isArchiveView: () => boolean;
  mergeConversationsPage: (rows: any[]) => void;
  refreshConversationsPage1: () => void;
  setContactsArchived: (contactIds: string[], archived: boolean) => Promise<boolean>;
  bulkArchiveSelected: (archived: boolean) => Promise<boolean>;
  toggleContactArchive: () => Promise<void>;
  applyContactArchiveUpdated: (payload: { contactId: string; archived: boolean }) => void;
}

const isArchiveFilter = (filters: any[] | undefined | null): boolean =>
  Array.isArray(filters) && filters.some((f) => f && f.type === 'top' && f.id === 'archive');

export interface SelectedContact {
  contact_id: string;
  name: string;
}

export interface BulkTagResult {
  added: number;
  skipped: number;
  invalid: string[];
  addedContactIds: string[];
}

const pluralContacts = (n: number) => `${n} contact${n === 1 ? '' : 's'}`;

// The inbox page keeps the selected company in React state and passes it
// into retrieveConversations; `state.companyId` is mirrored from there. Fall
// back to the open contact, then the login payload, so bulk actions never
// hit `/api/v3/inbox/tags/` with an empty id.
const resolveStoreCompanyId = (get: () => InboxV2State): string =>
  get().companyId
  || get().activeContactModel?.company_id
  || session.get('userData')?.companyId
  || '';

// Mirrors the backend GET visibility rules in
// resources/templates/handlers/get-all.js and api-v3/handlers/module-group/list.js
// (scope=owned): show the viewer's own private rows + the company's public/null
// rows. Used to decide whether a socket-pushed row belongs in the local list.
const isVisibleToViewer = (row: any, viewerId: string): boolean => {
  if (!row) return false;
  if (row.user_id === viewerId) return true;
  return row.privacy === 'public' || row.privacy == null;
}

import _ from "lodash";
import { message } from "antd";
import { showError, showSuccess } from './utils/errorMessages';
import session from '../../utils/session';

// The company-wide tag list (`state.tags`) is what the inbox filter menu is
// built from. Socket-driven tag changes used to patch only the contact and the
// conversation row, leaving that list stale — so a tag removed elsewhere kept
// showing in the filter and a tag added elsewhere never appeared.
//
// Debounced because bulk tagging emits one socket event per contact, and
// because the user who made the change also receives an echo of their own
// event, which would otherwise refetch twice. Lodash keeps the last args, so
// the most recent thunk wins.
const scheduleTagListRefresh = _.debounce((refresh: () => void) => refresh(), 1000);

// A bulk (un)archive fans out one socket event per contact; collapse the
// resulting "row should appear" refetches into a single page-1 request.
const schedulePage1Refresh = _.debounce((refresh: () => void) => refresh(), 400);
const scheduleListReload = _.debounce((reload: () => void) => reload(), 300);

export const useStore = create<InboxV2State>((set, get) => ({
  companyId: "",
  filters: [],
  campaigns: [],
  tags: [],
  groups: [],
  threads: [],
  userMessageTemplates:[],
  userSequenceTemplates:[],
  conversations: [],
  activeContact: "",
  activeContactModel: {},
  currentRoom: "",
  currentConversationRoom: "",
  calendarData: [],
  companyGroups: [],
  composerChannel: null,
  setComposerChannel: (channel) => set({ composerChannel: channel }),

  pushThread: (data,socketEvent=false)=>{
    let {threads,activeContact} = get()
    console.log("pushThread",data)
    //check that the incoming thread belongs to the activeContact

    let contactId: string = '';
    let threadData: any = {}
    switch(socketEvent){
      case 'threadUpdate':
        contactId = data.conversation.contact_id;
        // Compute channel_name from conversation.botmap (mirrors backend getChannelName logic)
        let channel_name: any = undefined;
        if (data.conversation?.botmap) {
          const botmap = data.conversation.botmap;
          if (['messagemedia', 'nexmo', 'twilio'].includes(botmap.type)) {
            if (['longcode', 'b-longcode'].includes(botmap.default)) {
              channel_name = { type: 'user', id: data.user_id || null, name: data.user?.name || '', avatar_url: data.user?.avatar_url || null };
            } else if (['broadcast', 'deleted-broadcast'].includes(botmap.default)) {
              channel_name = { type: 'system', id: null, name: 'Broadcast', avatar_url: null };
            } else if (botmap.default === null) {
              channel_name = { type: 'system', id: null, name: 'GoBe', avatar_url: null };
            }
          } else if (botmap.type === 'webchat') {
            channel_name = { type: 'bot', id: null, name: botmap.bot?.name || 'Chat', avatar_url: null };
          } else if (botmap.type === 'whatsapp') {
            // ADR-025: a user-sent WhatsApp reply is attributed like a longcode
            // text; bot/system traffic shows as the WhatsApp channel itself.
            channel_name = data.user?.id
              ? { type: 'user', id: data.user_id || data.user.id || null, name: data.user?.name || '', avatar_url: data.user?.avatar_url || null }
              : { type: 'bot', id: null, name: 'WhatsApp', avatar_url: null };
          }
        }
        threadData = {
          conversation_id: data.conversation_id,
          created_at: data.created_at,
          direction: data.direction,
          from: data.from,
          id: data.id,
          message: data.message,
          message_status: data.message_status,
          multipart: data.multipart,
          to: data.to,
          updated_at: data.updated_at,
          user_id: data.user_id,
          channel: data.channel,
          provider_code: data.provider_code,
          via: data.via,
          meta: data.meta,
          channel_name,
          conversation: data.conversation,
          user: data.user,
          campaign: data.campaign,
          attachments: data.attachments,
        }
        // ADR-035: a later threadUpdate for the same row (delivery receipt, status
        // change) may not carry the lifted translation — never clobber one we have.
        if (data.translation !== undefined) threadData.translation = data.translation;
      break;
      case 'scheduledMessage': {
        contactId = data.conversation.contact_id;
        const schedChannel = data.user
          ? {
              type: 'user',
              id: data.user.id || null,
              name: data.user.name || '',
              avatar_url: data.user.avatar_url || null,
            }
          : undefined;
        threadData = {
          id: data.id,
          conversation_id: data.conversation_id,
          direction: 'scheduled',
          created_at: data.created_at,
          message: data.message,
          isScheduled: true,
          user: data.user,
          channel_name: schedChannel ? schedChannel.name : '',
          channelDetails: schedChannel,
        }
        break;
      }
      default: contactId = data.contact.id;
      threadData = data.thread
    }
  

    // Fix the condition - should be !== not ===
    if(activeContact !== contactId){
      return;
    }
    
    // Check if thread already exists to prevent duplicates
    const existingThreadIndex = _.findIndex(threads, { id: threadData.id });

    if (existingThreadIndex !== -1) {
      // Update existing thread instead of adding duplicate
      const updatedThreads = [...threads];
      updatedThreads[existingThreadIndex] = { ...updatedThreads[existingThreadIndex], ...threadData };
      set({ threads: updatedThreads });
    } else {
      // Add new thread, sorted by created_at so future-dated scheduled bubbles
      // (event reminders, follow-ups) end up below just-now real messages.
      const next = _.sortBy([...threads, threadData], (t) => new Date(t.created_at).getTime());
      set({ threads: next });
    }
  },

  convoUpdate: (data)=>{
   console.log("convoUpdate from socket-->",data)
  },

  updateConversationWithNewMessage: (contactId, messageData, direction?) => {
    const { conversations } = get();

    // Find the conversation with the matching contact_id
    const conversationIndex = _.findIndex(conversations, { contact_id: contactId });

    console.log('[updateConvo]', { contactId, direction, found: conversationIndex !== -1, conversationCount: conversations.length, messageText: messageData?.message?.substring(0, 40) });

    if (conversationIndex !== -1) {
      const existingConversation = conversations[conversationIndex];

      // Check if the message is actually new/different
      if (existingConversation.message === messageData.message &&
          existingConversation.created_at === (messageData.created_at || existingConversation.created_at)) {
        console.log('[updateConvo] DEDUP — same message, skipping');
        return; // No update needed
      }
      
      // Remove the conversation from its current position
      const updatedConversations = [...conversations];
      const [conversation] = updatedConversations.splice(conversationIndex, 1);
      
      // Update the conversation with new message data
      const updatedConversation = {
        ...conversation,
        message: messageData.message, // Update the message field that the component uses
        created_at: messageData.created_at || new Date().toISOString(), // Update timestamp
        direction: direction || messageData.direction || conversation.direction, // Update direction if provided
        recentlyUpdated: true, // Mark as recently updated
        // Add any other fields that need updating
      };
      
      // Add it to the top of the list
      set({ conversations: [updatedConversation, ...updatedConversations] });

      // Clear the recentlyUpdated flag after 3 seconds
      setTimeout(() => {
        const currentConversations = get().conversations;
        const updatedIndex = _.findIndex(currentConversations, { contact_id: contactId });
        if (updatedIndex !== -1) {
          const updatedConversations = [...currentConversations];
          updatedConversations[updatedIndex] = {
            ...updatedConversations[updatedIndex],
            recentlyUpdated: false
          };
          set({ conversations: updatedConversations });
        }
      }, 3000);
    }
  },

  retrieveUserMessageTemplates: async()=> {
    const msgTemplates = await getUserMessageTemplates();
    //console.log("messageTe",msgTemplates)
    if (msgTemplates.statusCode == 200) {
      set({ userMessageTemplates: msgTemplates.data });
    }
  },
  retrieveSequenceTemplates: async () => {
    try {
      const res = await getSequenceTemplates();
      const data = res.data || res;
      const rows = data.rows || [];
      set({ userSequenceTemplates: rows });
    } catch (error) {
      console.error('Failed to fetch sequence templates:', error);
    }
  },

  applyTemplateCreated: (template) => {
    if (!template || !template.id) return;
    const viewerId = session.get('userData')?.id;
    if (!isVisibleToViewer(template, viewerId)) return;
    const { userMessageTemplates } = get();
    if (userMessageTemplates.some((t: any) => t.id === template.id)) return;
    // Match GET /api/v1/templates ordering: updated_at desc.
    const next = _.orderBy(
      [...userMessageTemplates, template],
      [(t: any) => t.updated_at || t.created_at || ''],
      ['desc'],
    );
    set({ userMessageTemplates: next });
  },
  applyTemplateUpdated: (template) => {
    if (!template || !template.id) return;
    const viewerId = session.get('userData')?.id;
    const { userMessageTemplates } = get();
    const idx = userMessageTemplates.findIndex((t: any) => t.id === template.id);
    const nowVisible = isVisibleToViewer(template, viewerId);
    if (idx === -1) {
      if (!nowVisible) return;
      const next = _.orderBy(
        [...userMessageTemplates, template],
        [(t: any) => t.updated_at || t.created_at || ''],
        ['desc'],
      );
      set({ userMessageTemplates: next });
      return;
    }
    if (!nowVisible) {
      set({ userMessageTemplates: userMessageTemplates.filter((t: any) => t.id !== template.id) });
      return;
    }
    const next = [...userMessageTemplates];
    next[idx] = template;
    set({ userMessageTemplates: _.orderBy(next, [(t: any) => t.updated_at || t.created_at || ''], ['desc']) });
  },
  applyTemplateDeleted: (templateId) => {
    if (!templateId) return;
    const { userMessageTemplates } = get();
    if (!userMessageTemplates.some((t: any) => t.id === templateId)) return;
    set({ userMessageTemplates: userMessageTemplates.filter((t: any) => t.id !== templateId) });
  },

  applySequenceCreated: (sequence) => {
    if (!sequence || !sequence.id) return;
    const viewerId = session.get('userData')?.id;
    if (!isVisibleToViewer(sequence, viewerId)) return;
    const { userSequenceTemplates } = get();
    if (userSequenceTemplates.some((s: any) => s.id === sequence.id)) return;
    // Match GET /api/v3/module-group?scope=owned&sort=created_at&order=desc.
    const next = _.orderBy(
      [...userSequenceTemplates, sequence],
      [(s: any) => s.created_at || ''],
      ['desc'],
    );
    set({ userSequenceTemplates: next });
  },
  applySequenceUpdated: (sequence) => {
    if (!sequence || !sequence.id) return;
    const viewerId = session.get('userData')?.id;
    const { userSequenceTemplates } = get();
    const idx = userSequenceTemplates.findIndex((s: any) => s.id === sequence.id);
    const nowVisible = isVisibleToViewer(sequence, viewerId);
    if (idx === -1) {
      if (!nowVisible) return;
      const next = _.orderBy(
        [...userSequenceTemplates, sequence],
        [(s: any) => s.created_at || ''],
        ['desc'],
      );
      set({ userSequenceTemplates: next });
      return;
    }
    if (!nowVisible) {
      set({ userSequenceTemplates: userSequenceTemplates.filter((s: any) => s.id !== sequence.id) });
      return;
    }
    const next = [...userSequenceTemplates];
    next[idx] = sequence;
    set({ userSequenceTemplates: next });
  },
  applySequenceDeleted: (sequenceId) => {
    if (!sequenceId) return;
    const { userSequenceTemplates } = get();
    if (!userSequenceTemplates.some((s: any) => s.id === sequenceId)) return;
    set({ userSequenceTemplates: userSequenceTemplates.filter((s: any) => s.id !== sequenceId) });
  },
  setActiveContact: async (contactId) => {
    const { currentRoom, currentConversationRoom, conversations, markConversationAsRead } = get();
    // Leave current rooms if they exist
    if (currentRoom) {
      ioLeaveRoom(currentRoom);
    }
    if (currentConversationRoom) {
      ioLeaveRoom(currentConversationRoom);
    }
    // Join new rooms if contactId is provided
    let conversationRoom = '';
    if (contactId) {
      joinRoom(contactId);
      // Also join conversation_id room so fetchThreads events (inbound messages) arrive
      const conversation = conversations.find((c: any) => c.contact_id === contactId);
      const convId = conversation?.conversation_id || conversation?.id;
      if (convId) {
        joinRoom(convId);
        conversationRoom = convId;
      }
    }
    // Set activeContact and currentRoom immediately so socket handlers
    // can match incoming messages to the correct contact without waiting
    // for the async getContactModel call to complete.
    set({ activeContact: contactId, currentRoom: contactId, currentConversationRoom: conversationRoom, threads: [], composerChannel: null });

    if (contactId.length) {
      const model = await getContactModel(contactId);
      // Guard: if user switched contacts while we were fetching, discard stale result
      if (get().activeContact !== contactId) return;
      set({
        activeContactModel: model.data,
      });
      // Mark notifications as read for this contact
      await markConversationAsRead(contactId);
      return;
    }
    console.log("clear selected contact")
    set({
      activeContactModel: {},
    });
  },
  updateSelectedCompany: (newCompany) =>
    set({
      companyId: newCompany,
      conversations: [],
      selectedContacts: new Map(),
      filters: [],
      tags: [],
      campaigns: [],
      groups: [],
      currentRoom: "",
      currentConversationRoom: "",
    }),
  retrieveFilters: async (companyId) => {
    try {
      const filters = await getInboxFilters(companyId);

      const locationUnkownFilter = {
        type: "location",
        label: "Not Specified",
        location_id: "location-null", //this is transformed to null on api call to conversations
      };

      set({ filters: [...filters.data, locationUnkownFilter] });
    } catch (error) {
      console.error('Failed to load filters:', error);
      showError('networkError');
    }
  },
  retrieveCampaigns: async (companyId) => {
    try {
      const campaigns = await getInboxCampaigns(companyId);
      set({ campaigns: [...campaigns.data] });
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      showError('networkError');
    }
  },
  retrieveTags: async (companyId) => {
    try {
      const tags = await getInboxTags(companyId);
      set({ tags: [...tags.data] });
    } catch (error) {
      console.error('Failed to load tags:', error);
      showError('networkError');
    }
  },
  retrieveGroups: async (companyId) => {
    try {
      const res = await getCompanyGroups(companyId);
      if (res.statusCode === 200) {
        const activeGroups = (res.data || []).filter((g: any) => !g.is_archived);
        set({ groups: activeGroups });
      }
    } catch (error) {
      console.error('Failed to load groups for filters:', error);
      showError('loadGroups');
    }
  },
  userHasCalendar:async ()=> {
    try {
      const calendarInfo =  await checkUserGroupDefault('calendar')
      if(calendarInfo.statusCode==200){
        set({ calendarData: calendarInfo.data });
        return calendarInfo.data
      }
      set({ calendarData: [] });
      return [];
    } catch (error) {
      console.error('Failed to check user calendar:', error);
      showError('calendarCheck');
      set({ calendarData: [] });
      return [];
    }
  },
  sendCalendarInvite: async (contactMobile,contactId, customMessage?, schedulingUserId?, translation?)=>{
    const payload: {[key: string]: any} = {to:contactMobile,contactId:contactId};
    if (customMessage) {
      payload.customMessage = customMessage;
    }
    // Scheduling-manager: book the interview onto a chosen teammate's calendar.
    if (schedulingUserId) {
      payload.schedulingUserId = schedulingUserId;
    }
    // ADR-035: customMessage is the previewed translation; record what was typed.
    if (translation) payload.translation = translation;
    return await sendInterviewScheduling(payload)
  },
  shortenUrl: async (url) => {
    const { activeContactModel } = get();
    if (!activeContactModel || !activeContactModel.id) {
      throw new Error('No active contact selected');
    }
    try {
      const resp = await shortenUrl({ url, contactId: activeContactModel.id, campaignId: null });
      // normalize return to the shortened url string
      const short = resp && resp.url;
      if (short) {
        showSuccess('urlShortened');
      }
      return short || null;
    } catch (err) {
      console.error('shortenUrl error', err);
      showError('shortenUrl');
      return null;
    }
  },
  sendMessage: async (body,botmapId,contactId,contactMobile,moduleGroup,translation,opts)=>{
    const data: {[key: string]: any} = {
      body,
      botmapId,
      contactId,
      moduleGroup: moduleGroup || '',
      sendType:'reply',
      to:contactMobile
    }
    // ADR-035: body is the previewed translation; this records what was typed.
    if (translation) data.translation = translation;
    // ADR-025: WhatsApp routing + what to do when the 24h window is closed.
    if (opts?.channel) data.channel = opts.channel;
    if (opts?.fallback) data.fallback = opts.fallback;
    return await sendReply(data)
  },
  cancelWhatsAppHold: async (pendingId) => {
    try {
      const res = await cancelWhatsAppHoldApi(pendingId);
      if (res.statusCode === 200) {
        set({ threads: _.filter(get().threads, (t) => t.id !== pendingId) });
        showSuccess('scheduledMessageDeleted', 'Held WhatsApp message cancelled');
        return true;
      }
    } catch (e) {
      // fall through
    }
    showError('deleteScheduledMessage', 'Failed to cancel the held message. Please try again.');
    return false;
  },

  // ── AI translation (ADR-035) ──
  applyThreadTranslation: ({ threadId, translation }) => {
    if (!threadId) return;
    const { threads } = get();
    const idx = threads.findIndex((t: any) => t.id === threadId);
    if (idx === -1) return;
    const next = [...threads];
    next[idx] = { ...next[idx], translation };
    set({ threads: next });
  },
  applyContactLanguageUpdated: ({ contactId, language, source }) => {
    const { activeContactModel } = get();
    if (!activeContactModel || activeContactModel.id !== contactId) return;
    set({ activeContactModel: { ...activeContactModel, language: language || null, language_source: source || null } });
  },
  setContactLanguage: async (language) => {
    const { activeContactModel } = get();
    if (!activeContactModel || !activeContactModel.id) return false;
    try {
      const res: any = await updateContactLanguageApi(activeContactModel.id, language);
      const data = (res && res.data) || {};
      set({
        activeContactModel: {
          ...activeContactModel,
          language: data.language !== undefined ? data.language : (language || null),
          language_source: data.language_source !== undefined ? data.language_source : (language ? 'manual' : null),
        },
      });
      return true;
    } catch (error) {
      console.error('Failed to set contact language:', error);
      showError('networkError');
      return false;
    }
  },
  translateThreadOnDemand: async (threadId, targetLang) => {
    const res: any = await translateThreadApi(threadId, targetLang ? { target_lang: targetLang } : {});
    const data = (res && res.data) || res || {};
    if (data.translation) get().applyThreadTranslation({ threadId, translation: data.translation });
    return data;
  },
  downloadContactPDF: async (contactId)=>{
    return await downloadContact(contactId)
  },
  deleteContactTag: async (tagId) => {
    let { activeContactModel, retrieveTags } = get();
    const deleted = await deleteContactTag(tagId);

    if (deleted.statusCode == 200) {
      //remove the tag from the contactModel
      const tags = _.filter(activeContactModel.tags, (tag) => tag.id != tagId);
      console.log("new tag list:", tags);
      activeContactModel.tags = [...tags];
      await retrieveTags(activeContactModel.company_id); //updates the tag filters
      set({ activeContactModel: { ...activeContactModel } });

      // Also update the conversations list so the message list shows updated tags
      const updatedConversations = get().conversations.map((c) => {
        if (c.contact_id === activeContactModel.id) {
          return {
            ...c,
            contactTagValues: _.map(activeContactModel.tags, (t) => ({ id: t.id, value: t.value })),
          };
        }
        return c;
      });

      set({ conversations: updatedConversations });
      showSuccess('tagRemoved');
    } else {
      showError('removeTag');
    }

    return deleted.statusCode == 200;
  },
  deleteScheduledMessageThread: async(schedMsgId) =>{
    let { threads } = get();
    const deleted = await deleteScheduledMessage(schedMsgId)

    if (deleted.statusCode == 200) {
      //remove the sched-message thread
      const updatedThreads = _.filter(threads, (thread) => thread.id != schedMsgId);
      set({ threads: [...updatedThreads] });
      showSuccess('scheduledMessageDeleted');
    } else {
      showError('deleteScheduledMessage');
    }
    return deleted.statusCode == 200;
  },
  cancelScheduledMessage: async (schedMsgId) => {
    const { threads } = get();
    try {
      const res = await cancelScheduledMessageApi(schedMsgId);
      if (res.statusCode === 200) {
        const updatedThreads = _.filter(threads, (t) => t.id !== schedMsgId);
        set({ threads: [...updatedThreads] });
        showSuccess('scheduledMessageDeleted');
        return true;
      }
    } catch (e) {
      // fall through
    }
    showError('deleteScheduledMessage');
    return false;
  },
  sendScheduledMessageNow: async (schedMsgId) => {
    try {
      const res = await sendScheduledMessageNowApi(schedMsgId);
      if (res.statusCode === 200) {
        // Bubble will naturally flip to sent once the scheduler tick processes it.
        // Mark it skipped in the interim so the UI reflects the user's intent immediately.
        const { threads } = get();
        const updatedThreads = threads.map((t) =>
          t.id === schedMsgId ? { ...t, scheduleNowRequested: true } : t,
        );
        set({ threads: [...updatedThreads] });
        return true;
      }
    } catch (e) {
      // fall through
    }
    showError('deleteScheduledMessage');
    return false;
  },
  muteContactEventReminders: async (contactId, muted) => {
    try {
      const res = await muteContactEventRemindersApi(contactId, muted);
      if (res.statusCode === 200) {
        if (muted) {
          const { threads } = get();
          const updatedThreads = _.filter(
            threads,
            (t) => !(t.kind === 'event_reminder' && t.direction === 'scheduled'),
          );
          set({ threads: [...updatedThreads] });
        }
        return true;
      }
    } catch (e) {
      // fall through
    }
    showError('deleteScheduledMessage');
    return false;
  },
  dropEnrollmentForContact: async (contactId, moduleId) => {
    // Placeholder: wire to the flow-enrollment drop endpoint when the caller
    // has the enrollment id. For the bubble-level action, defer to the existing
    // drop UI on the contact profile. Return false so callers know no-op.
    showError('deleteScheduledMessage');
    return false;
  },
  applyScheduledMessageSocketEvent: (event, payload) => {
    const { threads, activeContact } = get();
    if (
      event === 'scheduled_message:cancelled'
      || event === 'scheduled_message:sent'
      || event === 'scheduled_message:deleted'
    ) {
      // Bubble disappears on cancel / skip / send / delete. No 24h morph.
      set({ threads: _.filter(threads, (t) => t.id !== payload.id) });
    } else if (event === 'scheduled_message:rescheduled' && payload.schedule) {
      const next = threads.map((t) =>
        t.id === payload.id ? { ...t, created_at: payload.schedule } : t,
      );
      set({ threads: next });
    } else if (event === 'scheduled_message:created') {
      // Only append if the bubble belongs to the active contact's conversation
      // (the conversation_id room subscription should already filter this, but
      // double-check for safety) and isn't already in the threads array.
      if (payload && payload.id && !threads.some((t) => t.id === payload.id)) {
        const belongsToActive = !payload.conversation
          || !activeContact
          || payload.conversation.contact_id === activeContact;
        if (belongsToActive) {
          const next = _.sortBy([...threads, payload], (t) => new Date(t.created_at).getTime());
          set({ threads: next });
        }
      }
    }
  },
  addContactTag: async (value) => {
    let { activeContactModel, retrieveTags } = get();
    const added = await addContactTag(activeContactModel.id, value);

    if (added.statusCode == 200) {
      const model = await getContactModel(activeContactModel.id); //updatecontactmodel
      await retrieveTags(activeContactModel.company_id); //updates the tag filters
      set({ activeContactModel: model.data });

      // Also update the conversations list so the message list shows updated tags
      const updatedConversations = get().conversations.map((c) => {
        if (c.contact_id === model.data.id) {
          return {
            ...c,
            contactTagValues: _.map(model.data.tags, (t) => ({ id: t.id, value: t.value })),
          };
        }
        return c;
      });

      set({ conversations: updatedConversations });
      showSuccess('tagAdded');
    } else {
      showError('addTag');
    }

    return added.statusCode == 200;
  },
  toggleContactStar: async () => {
    let { activeContactModel, conversations } = get();

    const toggled = await setContactStar(
      activeContactModel.id,
      !activeContactModel.star
    );

    if (toggled.statusCode == 200) {
      const model = await getContactModel(activeContactModel.id); //updatecontactmodel

      const updatedConversations = conversations.map((c) => {
        if (c.contact_id === activeContactModel.id) {
          return { ...c, star: !activeContactModel.star };
        }
        return c;
      });

      set({
        activeContactModel: model.data,
        conversations: updatedConversations,
      });
      
      showSuccess(activeContactModel.star ? 'contactUnstarred' : 'contactStarred');
    } else {
      showError('starContact');
    }
  },
  retrieveContactThreads: async (contactId, page) => {
    try {
      const newThreads = await getContactThreads(contactId, `page=${page}`);

      set((state) => ({
        threads: _.orderBy(
          // Replace on page 1 so a refetch (e.g. after sequence enrollment) doesn't
          // duplicate bubbles already inserted by socket events. Dedup by id on
          // every merge as a defensive backstop for any other refresh path.
          page === 1
            ? _.uniqBy(newThreads.data, "id")
            : _.uniqBy([...state.threads, ...newThreads.data], "id"),
          ["created_at"],
          ["asc"]
        ),
      }));

      // On first page load, join conversation_id room if not already joined
      // (fallback for when conversations list didn't have conversation_id)
      if (page === 1 && newThreads.data.length > 0) {
        const { currentConversationRoom } = get();
        const convId = newThreads.data[0].conversation_id;
        if (convId && currentConversationRoom !== convId) {
          if (currentConversationRoom) ioLeaveRoom(currentConversationRoom);
          joinRoom(convId);
          set({ currentConversationRoom: convId });
        }
      }

      return newThreads.data.length < 15; //15 is default pageSize on API
    } catch (error) {
      console.error('Failed to load threads:', error);
      showError('loadThreads');
      return true; // Return true to stop further loading attempts
    }
  },
  clearThreads: () => {
    set({ threads: [] });
  },
  retrieveConversations: async (companyId, filters, page, pageSize) => {
    try {
      const conversationFilters = { filters };
      const newList = await getInboxConversationsList(
        conversationFilters,
        companyId,
        page,
        pageSize
      );
      set((state) => ({
        companyId: companyId || state.companyId,
        lastListQuery: { companyId: companyId || state.companyId, filters: filters || [], pageSize },
        conversations: page === 1 ? newList.data : [...state.conversations, ...newList.data],
      }));

      return newList.data.length < pageSize;
    } catch (error) {
      console.error('Failed to load conversations:', error);
      showError('loadConversations');
      return true; // Return true to stop further loading attempts
    }
  },
  clearConversations: () => {
    // Fires on every filter/menu change — a selection made under one filter
    // must not silently carry over to the next.
    set({ conversations: [], selectedContacts: new Map() });
  },
  selectedContacts: new Map(),
  toggleContactSelected: (row) => {
    if (!row || !row.contact_id) return;
    const next = new Map(get().selectedContacts);
    if (next.has(row.contact_id)) next.delete(row.contact_id);
    else next.set(row.contact_id, { contact_id: row.contact_id, name: row.name });
    set({ selectedContacts: next });
  },
  setContactsSelected: (rows, selected) => {
    const next = new Map(get().selectedContacts);
    (rows || []).forEach((row) => {
      if (!row || !row.contact_id) return;
      if (selected) next.set(row.contact_id, { contact_id: row.contact_id, name: row.name });
      else next.delete(row.contact_id);
    });
    set({ selectedContacts: next });
  },
  clearSelection: () => {
    if (get().selectedContacts.size === 0) return;
    set({ selectedContacts: new Map() });
  },
  bulkAddTagToSelected: async (value) => {
    const { selectedContacts, retrieveTags, clearSelection } = get();
    const contactIds = [...selectedContacts.keys()];
    const trimmed = (value || '').trim();
    if (contactIds.length === 0 || !trimmed) return null;
    try {
      const res = await bulkAddContactTags(contactIds, trimmed);
      if (res.statusCode !== 200) {
        showError('addTag');
        return null;
      }
      const result: BulkTagResult = res.data;
      // Rows repaint via the per-contact `contact:tags-updated` socket events
      // (applyContactTagsUpdated); only the filter menu's tag list needs a pull.
      const cid = resolveStoreCompanyId(get);
      if (cid) await retrieveTags(cid);
      clearSelection();
      const parts = [`Tag "${trimmed}" added to ${pluralContacts(result.added)}`];
      if (result.skipped > 0) parts.push(`${result.skipped} already had it`);
      if (result.invalid && result.invalid.length > 0) parts.push(`${result.invalid.length} skipped (no access)`);
      showSuccess('tagAdded', parts.join(' · '));
      return result;
    } catch (error) {
      console.error('Failed to bulk add tag:', error);
      showError('addTag');
      return null;
    }
  },
  bulkAddSelectedToGroup: async (groupId, groupName) => {
    const { selectedContacts, groups, clearSelection } = get();
    const contactIds = [...selectedContacts.keys()];
    if (contactIds.length === 0 || !groupId) return false;
    try {
      const res = await addGroupContacts(groupId, contactIds);
      if (res.statusCode !== 200) {
        showError('addGroup');
        return false;
      }
      // add-group-contacts replies { statusCode, message, added } which the
      // onSend hook wraps once more, so the count lives at res.data.added.
      const added = typeof res.data?.added === 'number' ? res.data.added : contactIds.length;
      const name = groupName || groups.find((g: any) => g.id === groupId)?.name;
      clearSelection();
      const label = name ? `"${name}"` : 'group';
      const suffix = added < contactIds.length ? ` (${contactIds.length - added} already in it)` : '';
      showSuccess('groupAdded', `${pluralContacts(added)} added to ${label}${suffix}`);
      return true;
    } catch (error) {
      console.error('Failed to bulk add contacts to group:', error);
      showError('addGroup');
      return false;
    }
  },
  bulkCreateGroupWithSelected: async (name) => {
    const { selectedContacts, retrieveGroups, clearSelection } = get();
    const contactIds = [...selectedContacts.keys()];
    const trimmed = (name || '').trim();
    if (contactIds.length === 0 || !trimmed) return false;
    try {
      const res = await createGroup(trimmed, contactIds);
      if (res.statusCode !== 201 && res.statusCode !== 200) {
        showError('createGroup');
        return false;
      }
      const group = res.data;
      // create-group emits no socket event, so patch the rows locally.
      if (group && group.id) {
        set((state) => ({
          conversations: state.conversations.map((c: any) => {
            if (!selectedContacts.has(c.contact_id)) return c;
            const existing = c.contactGroups || [];
            if (existing.some((g: any) => g.id === group.id)) return c;
            return { ...c, contactGroups: [...existing, { id: group.id, name: group.name || trimmed }] };
          }),
        }));
      }
      const cid = resolveStoreCompanyId(get);
      if (cid) await retrieveGroups(cid); // updates the Groups filter menu + picker
      clearSelection();
      showSuccess('groupCreated', `Group "${trimmed}" created with ${pluralContacts(contactIds.length)}`);
      return true;
    } catch (error) {
      console.error('Failed to create group from selection:', error);
      showError('createGroup');
      return false;
    }
  },
  removeConversation: (contactId) => {
    const { conversations } = get();
    const filtered = conversations.filter(conv => conv.contact_id !== contactId);
    set({ conversations: filtered });
  },
  markConversationAsRead: async (contactId) => {
    const { conversations } = get();
    
    // Find the conversation and check if it has unread notifications
    const conversation = conversations.find(c => c.contact_id === contactId);
    const hadUnread = conversation?.hasUnreadNotification;
    
    // Update conversation in the store to mark as read
    const updatedConversations = conversations.map(c => {
      if (c.contact_id === contactId) {
        return { ...c, hasUnreadNotification: false };
      }
      return c;
    });
    set({ conversations: updatedConversations });
    
    // Call API to mark notifications as read
    try {
      const result = await readContactNotifications(contactId);
      // Return the count of notifications marked as read (for redux store update)
      return result?.data?.updatedCount || (hadUnread ? 1 : 0);
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
      showError('markAsRead');
      return 0;
    }
  },
  fetchCompanyGroups: async () => {
    const { activeContactModel } = get();
    if (!activeContactModel || !activeContactModel.company_id) return;
    try {
      const res = await getCompanyGroups(activeContactModel.company_id, activeContactModel.id);
      if (res.statusCode === 200) {
        set({ companyGroups: res.data });
      }
    } catch (error) {
      console.error('Failed to fetch company groups:', error);
      showError('loadGroups');
    }
  },
  addContactToGroup: async (groupId) => {
    const { activeContactModel } = get();
    try {
      const res = await addContactToGroups(activeContactModel.id, [groupId]);
      if (res.statusCode === 200) {
        const model = await getContactModel(activeContactModel.id);
        set({ activeContactModel: model.data });
        showSuccess('groupAdded');
        return true;
      }
      showError('addGroup');
      return false;
    } catch (error) {
      console.error('Failed to add contact to group:', error);
      showError('addGroup');
      return false;
    }
  },
  removeContactFromGroup: async (groupId) => {
    const { activeContactModel } = get();
    try {
      const res = await removeContactFromGroups(activeContactModel.id, [groupId]);
      if (res.statusCode === 200) {
        const updatedGroups = (activeContactModel.groups || []).filter((g) => g.id !== groupId);
        set({ activeContactModel: { ...activeContactModel, groups: updatedGroups } });
        showSuccess('groupRemoved');
        return true;
      }
      showError('removeGroup');
      return false;
    } catch (error) {
      console.error('Failed to remove contact from group:', error);
      showError('removeGroup');
      return false;
    }
  },
  createGroupAndAddContact: async (name) => {
    const { activeContactModel, retrieveGroups } = get();
    try {
      const res = await createGroup(name, [activeContactModel.id]);
      if (res.statusCode === 201) {
        const model = await getContactModel(activeContactModel.id);
        set({ activeContactModel: model.data });
        await retrieveGroups(activeContactModel.company_id); //updates the group filters
        showSuccess('groupCreated');
        return true;
      }
      showError('createGroup');
      return false;
    } catch (error) {
      console.error('Failed to create group:', error);
      showError('createGroup');
      return false;
    }
  },
  applyContactTagsUpdated: ({ contactId, companyId, tags }) => {
    const { activeContactModel, conversations } = get();
    const next: any = {};
    if (activeContactModel && activeContactModel.id === contactId) {
      next.activeContactModel = { ...activeContactModel, tags: tags || [] };
    }
    const idx = _.findIndex(conversations, { contact_id: contactId });
    if (idx !== -1) {
      const updated = [...conversations];
      updated[idx] = {
        ...updated[idx],
        contactTagValues: (tags || []).map((t: any) => ({ id: t.id, value: t.value })),
      };
      next.conversations = updated;
    }
    if (Object.keys(next).length) set(next);

    // Keep the filter menu's tag list in step with the change. Runs even when
    // the contact isn't in the current view: a tag can be added to or removed
    // from any contact in the company and still change the filter options.
    const cid =
      companyId || activeContactModel?.company_id || session.get('userData')?.companyId;
    if (cid) scheduleTagListRefresh(() => get().retrieveTags(cid));
  },
  applyContactStarUpdated: ({ contactId, star }) => {
    const { activeContactModel, conversations } = get();
    const next: any = {};
    if (activeContactModel && activeContactModel.id === contactId) {
      next.activeContactModel = { ...activeContactModel, star: !!star };
    }
    const idx = _.findIndex(conversations, { contact_id: contactId });
    if (idx !== -1) {
      const updated = [...conversations];
      updated[idx] = { ...updated[idx], star: !!star };
      next.conversations = updated;
    }
    if (Object.keys(next).length) set(next);
  },

  // ---- Per-user inbox Archive (ADR-034) ----------------------------------
  lastListQuery: null,
  listReloadToken: 0,
  requestListReload: () => {
    scheduleListReload(() => set((state) => ({ listReloadToken: state.listReloadToken + 1 })));
  },
  isArchiveView: () => isArchiveFilter(get().lastListQuery?.filters),
  mergeConversationsPage: (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const { conversations } = get();
    const incoming = rows.filter((r) => r && r.contact_id);
    const kept = conversations.filter(
      (c) => !incoming.some((r) => r.contact_id === c.contact_id),
    );
    const merged = [...incoming, ...kept].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    set({ conversations: merged });
  },
  refreshConversationsPage1: () => {
    schedulePage1Refresh(async () => {
      const { lastListQuery, mergeConversationsPage } = get();
      if (!lastListQuery || !lastListQuery.companyId) return;
      try {
        const res = await getInboxConversationsList(
          { filters: lastListQuery.filters },
          lastListQuery.companyId,
          1,
          lastListQuery.pageSize,
        );
        mergeConversationsPage(res?.data || []);
      } catch (error) {
        console.error('Failed to refresh conversations:', error);
      }
    });
  },
  setContactsArchived: async (contactIds, archived) => {
    const ids = (contactIds || []).filter(Boolean);
    if (ids.length === 0) return false;
    try {
      const res = await setContactsArchivedApi(ids, archived);
      if (res.statusCode !== 200) {
        showError('archiveContact');
        return false;
      }
      const { updated, invalid } = res.data || {};
      // Rows that no longer belong to the current view leave immediately; the
      // socket echo that follows is idempotent (applyContactArchiveUpdated).
      const {
        isArchiveView, activeContact, activeContactModel, removeConversation, setActiveContact, requestListReload,
      } = get();
      const visibleHere = isArchiveView() ? archived : !archived;
      if (!visibleHere) {
        ids.forEach((id) => removeConversation(id));
        if (activeContact && ids.includes(activeContact)) setActiveContact('');
        requestListReload(); // pull the rows that now fill the gap
      } else if (activeContactModel && ids.includes(activeContactModel.id)) {
        set({ activeContactModel: { ...activeContactModel, archived } });
      }
      const count = typeof updated === 'number' ? updated : ids.length;
      const suffix = invalid && invalid.length > 0 ? ` · ${invalid.length} skipped (no access)` : '';
      showSuccess(
        archived ? 'conversationArchived' : 'conversationUnarchived',
        `${pluralContacts(count)} ${archived ? 'archived' : 'moved back to Inbox'}${suffix}`,
      );
      return true;
    } catch (error) {
      console.error('Failed to update archive:', error);
      showError('archiveContact');
      return false;
    }
  },
  bulkArchiveSelected: async (archived) => {
    const { selectedContacts, setContactsArchived, clearSelection } = get();
    const ok = await setContactsArchived([...selectedContacts.keys()], archived);
    if (ok) clearSelection();
    return ok;
  },
  toggleContactArchive: async () => {
    const { activeContactModel, setContactsArchived } = get();
    if (!activeContactModel || !activeContactModel.id) return;
    await setContactsArchived([activeContactModel.id], !activeContactModel.archived);
  },
  applyContactArchiveUpdated: ({ contactId, archived }) => {
    if (!contactId) return;
    const {
      conversations, activeContact, activeContactModel, isArchiveView,
      removeConversation, setActiveContact, refreshConversationsPage1, requestListReload,
    } = get();
    if (activeContactModel && activeContactModel.id === contactId) {
      set({ activeContactModel: { ...activeContactModel, archived: !!archived } });
    }
    const shouldBeVisible = isArchiveView() ? !!archived : !archived;
    const present = conversations.some((c) => c.contact_id === contactId);
    if (!shouldBeVisible && present) {
      removeConversation(contactId);
      // The row is gone from the list; keeping its thread open would let the
      // user reply to a conversation they can no longer locate.
      if (activeContact === contactId) setActiveContact('');
      requestListReload();
    } else if (shouldBeVisible && !present) {
      refreshConversationsPage1();
    }
  },
  applyUserAvatarUpdated: ({ userId, avatar_url }) => {
    if (!userId) return;
    const { activeContactModel, conversations, threads } = get();
    const next: any = {};

    // Conversation rows: avatar visible via `interactionDetails` AND legacy
    // `channel_name`. Patch both for completeness.
    const conversationsChanged = conversations.some(
      (c: any) =>
        (c?.channel_name?.type === 'user' && c.channel_name.id === userId) ||
        (Array.isArray(c?.interactionDetails) && c.interactionDetails.some((i: any) => i?.id === userId)),
    );
    if (conversationsChanged) {
      next.conversations = conversations.map((c: any) => {
        const updated: any = { ...c };
        if (c?.channel_name?.type === 'user' && c.channel_name.id === userId) {
          updated.channel_name = { ...c.channel_name, avatar_url };
        }
        if (Array.isArray(c?.interactionDetails)
          && c.interactionDetails.some((i: any) => i?.id === userId)) {
          updated.interactionDetails = c.interactionDetails.map((i: any) =>
            i && i.id === userId ? { ...i, avatar_url } : i,
          );
        }
        return updated;
      });
    }

    if (activeContactModel?.channel_name?.type === 'user' && activeContactModel.channel_name.id === userId) {
      next.activeContactModel = {
        ...activeContactModel,
        channel_name: { ...activeContactModel.channel_name, avatar_url },
      };
    }

    // Thread bubbles read `channelDetails` (scheduled) or object `channel_name`
    // (regular). Patch all three locations where the user appears.
    const threadsChanged = threads.some(
      (t: any) =>
        t?.user_id === userId ||
        t?.user?.id === userId ||
        (t?.channelDetails?.type === 'user' && t.channelDetails.id === userId) ||
        (t?.channel_name?.type === 'user' && t.channel_name.id === userId),
    );
    if (threadsChanged) {
      next.threads = threads.map((t: any) => {
        const matchesUser = t?.user_id === userId || t?.user?.id === userId;
        const matchesChannelDetails = t?.channelDetails?.type === 'user' && t.channelDetails.id === userId;
        const matchesChannelName =
          t?.channel_name && typeof t.channel_name === 'object'
            && t.channel_name.type === 'user' && t.channel_name.id === userId;
        if (!matchesUser && !matchesChannelDetails && !matchesChannelName) return t;
        return {
          ...t,
          ...(matchesUser && t.user ? { user: { ...t.user, avatar_url } } : {}),
          ...(matchesChannelDetails ? { channelDetails: { ...t.channelDetails, avatar_url } } : {}),
          ...(matchesChannelName ? { channel_name: { ...t.channel_name, avatar_url } } : {}),
        };
      });
    }

    if (Object.keys(next).length) set(next);
  },
  applyContactGroupsUpdated: ({ contactId, groups }) => {
    const { activeContactModel, conversations } = get();
    // Filter private groups not owned by the current viewer — backend broadcasts
    // every non-deleted group; visibility is per-user.
    const userId: string | undefined = session.get('userData')?.id;
    const visible = (groups || []).filter((g: any) =>
      g.privacy !== 'private' || (userId && g.user_id === userId),
    );
    const slim = visible.map((g: any) => ({ id: g.id, name: g.name }));

    const next: any = {};
    if (activeContactModel && activeContactModel.id === contactId) {
      next.activeContactModel = { ...activeContactModel, groups: slim };
    }
    const idx = _.findIndex(conversations, { contact_id: contactId });
    if (idx !== -1) {
      const updated = [...conversations];
      updated[idx] = { ...updated[idx], contactGroups: slim };
      next.conversations = updated;
    }
    if (Object.keys(next).length) set(next);
  },
}));
